import React, { useContext, useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { ButtonToolbar, Container, Row, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import { useLocation, useNavigate } from 'react-router';
import {
  PageHeader,
  Section,
  SpinnerArea,
  Button,
  Spinner,
  Modal,
  ConfirmModal,
  CheckBox,
  CustomDropDown,
} from '../../../../shared/components';
import { UserOperations } from '../../../../services/types/Admin';
import { OptionValue } from '../../../../services/types/Misc';
import FormDropdown from '../../../../shared/components/form/FormDropdown';
import useService from '../../../../shared/hooks/useService';
import OperationContext from '../context';
import MainContext from '../../../App/MainProvider/context';
import RouteLeavingGuard from '../../../App/RouteLeavingGuard';
import indicatorsService from '../../../../services/operIndicatorsService';
import OperationSubtitle from '../OperationSubtitle';
import useQuery from '../../../../shared/hooks/useQuery';
import { EsgIndicatorDto, IndicatorTopicDto } from '../../../../services/types/EsgIndicators';
import LastValueButton from './LastValueButton';
import IndicatorTimeline from './IndicatorTimeline';
import Textarea from '../../../../shared/components/Textarea';
import { BasicNotification } from '../../../../shared/components/Notification';
import Role from '../../../../types/role';
import OptionChecks from '../../OptionChecks';

const NO_INDICATORS_SELECTED = 'No indicators selected for saving';
const DROPDOWN_DISABLED_CLASS = 'dropdown-indicator-disabled';

export const buildIndicatorsSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    indicators: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().required(t('required')).nullable(),
        rationale: Yup.string()
          .trim()
          .when(['requiredRationale', 'enabled', 'newValue'], {
            is: (requiredRationale: boolean, enabled: boolean, newValue: string) =>
              requiredRationale && enabled && newValue,
            then: (schema) => schema.concat(Yup.string().required(t('required'))),
            otherwise: (schema) => schema.concat(Yup.string().nullable()),
          }),
      })
    ),
  });

const Indicator: React.FC = () => {
  const { operation, loading } = useContext(OperationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { service, notify, inSuperUserMode, inGuidanceServiceMode, user } = useContext(MainContext);
  const isViewer = user.roleCode === Role.Viewer;
  const canEdit =
    operation.isMember || inSuperUserMode() || (operation.belongsGS && inGuidanceServiceMode());

  const [data, loading1] = useService(async () =>
    indicatorsService.getIndicatorsByOperation(operation.id)
  );

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseErrors] = useState<UserOperations[] | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [dataForSubmit, setDataForSubmit] = useState<EsgIndicatorDto[] | null>([]);
  const queryParams = useQuery();
  const from = queryParams.get('from') || undefined;
  const title = `Operation ${operation.number} / Indicators`;
  const [excelLoading, setExcelLoading] = useState(false);

  const TypeValue = {
    Z07: '0-7',
    INTEGER: 'Integer',
    DECIMALS2: '2Decimals',
    PERCENT: 'Percent',
    YESNO: 'YesNo',
    SPR: 'SprRating',
    HAZARDS: 'Hazards',
  };

  const ConstValue = {
    NA: 'N/A',
  };

  const renderLastValue = (lastValue: string, type: string) => {
    if (lastValue && lastValue.length > 0) {
      if (type === TypeValue.SPR) {
        return lastValue.substring(1);
      }
      if (type === TypeValue.DECIMALS2) {
        if (lastValue === 'N/A') {
          return 'N/A';
        }
        return parseFloat(lastValue).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return lastValue;
    }
    return 'Not Set';
  };

  function refreshPage() {
    setTimeout(() => {
      window.location.href = `${window.location.href}?refresh=${new Date().getTime()}`;
    }, 500);
  }
  const sprElements: OptionValue[] = [
    { id: 100, text: 'Select', value: '' },
    { id: 1, text: 'Satisfactory', value: '1Satisfactory' },
    { id: 2, text: 'Partially Satisfactory', value: '2Partially Satisfactory' },
    { id: 3, text: 'Partially Unsatisfactory', value: '3Partially Unsatisfactory' },
    { id: 4, text: 'Unsatisfactory', value: '4Unsatisfactory' },
  ];

  const hazardsElements: OptionValue[] = [
    { id: 1, text: 'Earthquake', value: '1' },
    { id: 2, text: 'Landslides', value: '2' },
    { id: 3, text: 'Soil Erosion', value: '3' },
    { id: 4, text: 'Coastal Erosion', value: '4' },
    { id: 5, text: 'Fluvial Erosion', value: '5' },
    { id: 6, text: 'Tsunami', value: '6' },
    { id: 7, text: 'Volcanic', value: '7' },
    { id: 8, text: 'Subsidence', value: '8' },
    { id: 9, text: 'Drought', value: '9' },
    { id: 10, text: 'Frost-Cold Wave', value: '10' },
    { id: 11, text: 'Hailstorm', value: '11' },
    { id: 12, text: 'Storm Surge', value: '12' },
    { id: 13, text: 'Fluvial Flooding', value: '13' },
    { id: 14, text: 'Pluvial Flooding', value: '14' },
    { id: 15, text: 'Tidal Wave', value: '15' },
    { id: 16, text: 'Extreme Winds', value: '16' },
    { id: 17, text: 'Hurricane-Wind', value: '17' },
    { id: 18, text: 'Tropical Storm', value: '18' },
    { id: 19, text: 'Heat Wave ', value: '19' },
    { id: 20, text: 'Glacial Retreat', value: '20' },
    { id: 21, text: 'Sea Level Rise', value: '21' },
    { id: 22, text: 'Heavy Rains', value: '22' },
    { id: 23, text: 'Water Scarcity', value: '23' },
    { id: 24, text: 'Wildfire', value: '24' },
  ];

  const [checkedHazards, setCheckedHazards] = useState<string[]>([]);
  const [loadHarzards, setLoadHarzards] = useState<boolean>(false);
  const [indicatorWithErrors, setindicatorWithErrors] = useState<number[]>([]);

  const setCheckedValues = (id: string, checked: boolean): string => {
    let updatedHiddenOptions: string[] = checkedHazards.slice();

    if (!checked) {
      const selectdItem = checkedHazards.find((colId) => colId === id);
      if (selectdItem) {
        updatedHiddenOptions = checkedHazards.filter((item) => item !== id).sort();
        setCheckedHazards(updatedHiddenOptions);
      }
    } else {
      const selectdItem = checkedHazards.find((colId) => colId === id);
      if (!selectdItem) {
        updatedHiddenOptions = [...checkedHazards, id].sort();
        setCheckedHazards(updatedHiddenOptions);
      }
    }
    return updatedHiddenOptions.join(',');
  };

  const setHazardsInitialForm = (listValues: string): string => {
    if (listValues === ConstValue.NA) {
      return ConstValue.NA;
    }

    const valuesOptions = Array.from(new Set(listValues.split(',')));
    const selectedHazards = hazardsElements.filter(
      ({ value }) => value !== undefined && valuesOptions.includes(value)
    );

    if (selectedHazards.length > 0) {
      const selectedTexts = selectedHazards.map(({ text }) => text);
      const selectedIds = selectedHazards.map(({ value }) => value as string);

      const resultString = selectedTexts.join(', ');
      const maxLength = 6;
      const truncatedString =
        resultString.length > maxLength
          ? `${resultString.substring(0, maxLength)}...`
          : resultString;

      if (!loadHarzards) {
        setLoadHarzards(true);

        selectedTexts.forEach((elemento) => {
          setCheckedValues(elemento, true);
        });

        setCheckedHazards(selectedIds);
      }

      return truncatedString;
    }

    return t('Select');
  };

  const number07: OptionValue[] = [
    { id: 100, text: 'Select', value: '' },
    { id: 0, text: '0', value: '0' },
    { id: 1, text: '1', value: '1' },
    { id: 2, text: '2', value: '2' },
    { id: 3, text: '3', value: '3' },
    { id: 4, text: '4', value: '4' },
    { id: 5, text: '5', value: '5' },
    { id: 6, text: '6', value: '6' },
    { id: 7, text: '7', value: '7' },
  ];

  const yesNoElements: OptionValue[] = [
    { id: 100, text: 'Select', value: '' },
    { id: 1, text: 'Yes', value: '1' },
    { id: 0, text: 'No', value: '0' },
  ];

  const optionNa: OptionValue[] = [{ id: 0, text: ConstValue.NA, value: ConstValue.NA }];

  const determinePlaceholder = (enabled: boolean, newValue: string) => {
    if (enabled) {
      return newValue === ConstValue.NA ? ConstValue.NA : 'Type Value';
    }
    return '';
  };

  const discardChanges = () => {
    setShowDiscardModal(true);
  };

  const validateGHGCalculatorIndicators = (indicators: EsgIndicatorDto[]): boolean => {
    const ghgIndicators = indicators.filter(
      (indicator) => indicator.esgIndicatorId === 8 || indicator.esgIndicatorId === 9
    );

    return ghgIndicators.every((indicator) => {
      if (indicator.lastValue && indicator.lastValue.trim() !== '') {
        return true;
      }
      return (
        indicator.newValue && indicator.newValue.trim() !== '' && indicator.newValue !== 'Not Set'
      );
    });
  };

  const sendIndicators = () => {
    if (dataForSubmit) {
      if (!validateGHGCalculatorIndicators(dataForSubmit)) {
        notify(
          t('indicators'),
          'There are pending GHG Calculations that have not been submitted. Please review before sending ESG Indicators.',
          'warning'
        );
        return;
      }

      service(() => indicatorsService.sendIndicators(operation.id, dataForSubmit), {
        callback: () => {
          setFormDirty(false);
          setDataForSubmit(null);
          notify(t('indicators'), 'Saved Successfully', 'success');
          refreshPage();
        },
        loader: setIsSubmitting,
      });
    } else {
      notify(t('indicators'), NO_INDICATORS_SELECTED, 'warning');
    }
  };

  const saveIndicators = (
    formik: FormikProps<{
      blocked: boolean;
      flowTypeBlockedBy: string;
      stepBlockedBy: string;
      valuesForSend: boolean;
      indicators: EsgIndicatorDto[];
      topics: IndicatorTopicDto[];
    }>
  ) => {
    if (dataForSubmit) {
      const formValues = formik.values;
      const indicatorsForSubmit = formValues.indicators.filter((x) => x.enabled);
      setDataForSubmit(indicatorsForSubmit);

      if (indicatorsForSubmit.length > 0) {
        service(() => indicatorsService.saveIndicators(operation.id, indicatorsForSubmit), {
          callback: () => {
            setFormDirty(false);
            notify(t('indicators'), 'Saved Successfully', 'success');
            refreshPage();
          },
          loader: setIsSubmitting,
        });
      } else {
        notify(t('indicators'), NO_INDICATORS_SELECTED, 'warning');
      }
    } else {
      notify(t('indicators'), NO_INDICATORS_SELECTED, 'warning');
    }
  };

  const renderErrorTable = () => {
    if (!responseErrors || responseErrors.length === 0) {
      return null;
    }
    return responseErrors.map((x, i) => (
      <tr key={`responseError_tr${i.toString()}`}>
        <td className="text-nowrap">{x.user}</td>
        <td>{parse(x.operations.reduce((acc, v) => `${acc}, <nobr>${v}</nobr>`))}</td>
      </tr>
    ));
  };

  const exportExcel = () => {
    if (data && data.indicators[0].operationId !== 0) {
      service(() => indicatorsService.exportExcel(data.indicators[0].operationId), {
        loader: setExcelLoading,
      });
    }
  };

  const [, setValuesForSendForm] = useState(false);
  const [opDccr, setOpDccr] = useState('');

  useEffect(() => {
    if (data && data.indicators) {
      setValuesForSendForm(data.indicators.some((item) => item.newValue !== ''));
      setOpDccr(data.disasterRisk);
    }
  }, [data]);

  const Validation41 = (value: string) => {
    if (opDccr === 'Low' && value !== ConstValue.NA) {
      return 'DCCRC=Low, Should be N/A';
    }

    if ((opDccr === 'Moderate' || opDccr === 'High') && value === ConstValue.NA) {
      return `DCCRC=M/H, Can't be N/A`;
    }

    return true;
  };

  const onChangeValidation41 = (value: string, code: number) => {
    const errorValidation = Validation41(value) !== true;

    if (errorValidation) {
      indicatorWithErrors.push(code);
      setindicatorWithErrors([...indicatorWithErrors]);
    } else {
      const updatedList = indicatorWithErrors.filter((item) => item !== code);
      setindicatorWithErrors(updatedList);
    }
  };

  const customValidation = (type: string, value: string) => {
    let regex;

    if (value === ConstValue.NA || value === '' || value === null) {
      return true;
    }
    switch (type) {
      case TypeValue.INTEGER:
        regex = /^\d+$/;
        if (!regex.test(value)) {
          return 'Must be an integer';
        }
        break;
      case TypeValue.DECIMALS2:
        regex = /^[0-9]*(\.[0-9]{2})?$/;
        if (!regex.test(value)) {
          return 'Number with two decimal places';
        }
        break;
      case TypeValue.PERCENT:
        regex = /^[0-9]*(\.[0-9]{2})?$/;
        if (!regex.test(value)) {
          return 'Number with two decimal places';
        }
        break;
      default:
        break;
    }
    return true;
  };

  const getInputClass = (type: string, value: string) =>
    `custom-filter-form ${
      customValidation(type, value) !== true && value !== '' ? 'form-error-box' : ''
    }`;

  const onChangeValidation = (type: string, value: string, code: number) => {
    const validation = customValidation(type, value);

    if (validation === true) {
      const updatedList = indicatorWithErrors.filter((item) => item !== code);
      setindicatorWithErrors(updatedList);
    } else {
      indicatorWithErrors.push(code);
      setindicatorWithErrors([...indicatorWithErrors]);
    }
  };

  return (
    <>
      <RouteLeavingGuard
        when={formDirty}
        shouldBlockNavigation={() => formDirty}
        from={location}
        message={t('dirtyIndicatorsPopupMsg')}
      />
      <Container className="rolesAndPermissions">
        <PageHeader title={title} backUrl={from}>
          <OperationSubtitle />
        </PageHeader>
        <Row>
          <Section>
            <SpinnerArea loading={loading || excelLoading || loading1}>
              {data && (
                <Formik
                  initialValues={{
                    blocked: data ? data.blocked : false,
                    flowTypeBlockedBy: data ? data.flowTypeBlockedBy : '',
                    stepBlockedBy: data ? data.stepBlockedBy : '',
                    valuesForSend: data ? data.valuesForSend : false,
                    topics:
                      data && data.topics
                        ? data.topics.map((x) => ({
                            id: x.id,
                            description: x.description,
                          }))
                        : [],
                    indicators:
                      data && data.indicators
                        ? data.indicators.map((x) => ({
                            id: x.id,
                            formId: x.formId,
                            name: x.name,
                            description: x.description,
                            lastValue: x.lastValue,
                            newValue: x.newValue,
                            typeValue: x.typeValue,
                            requiredRationale: x.requiredRationale,
                            rationale: x.rationale,
                            timeLineContainer: x.timeLineContainer,
                            enabled: canEdit ? x.enabled : false,
                            belongsCalculator: x.belongsCalculator,
                            operationId: x.operationId,
                            esgIndicatorId: x.esgIndicatorId,
                            espsId: x.espsId,
                          }))
                        : [],
                  }}
                  validationSchema={buildIndicatorsSchema(t)}
                  onSubmit={(values) => {
                    setDataForSubmit(values.indicators);
                    setShowConfirmModal(true);
                  }}
                  enableReinitialize
                >
                  {(formik) => {
                    const { values, dirty } = formik;
                    return (
                      <>
                        <div className="button-container-bar bar-indicator-index">
                          <div className="mr-auto">
                            <h4 className="index-indicator-title">{t('fillIndicators')}</h4>
                          </div>
                          <Button
                            htmlId="btnExcel"
                            text={t('exportToExcel')}
                            onClick={exportExcel}
                            icon="file-excel"
                            className="btn-tertiary"
                          />
                        </div>
                        {formik.values.blocked && !isViewer && (
                          <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                            <BasicNotification
                              message={t('flowIndicatorsEdit', {
                                flowTypeAn: t(`stepBlockedByAn.${formik.values.flowTypeBlockedBy}`),
                                step: t(`flowStatusCodes.${formik.values.stepBlockedBy}`),
                                flowType: t(`stepBlockedBy.${formik.values.flowTypeBlockedBy}`),
                              })}
                              color="warning"
                              icon="exclamation-triangle"
                            />
                          </div>
                        )}
                        <form className="form" onSubmit={formik.handleSubmit}>
                          <div className="table-wrapper sticky-section w-100">
                            <Table className="indicators-table table--bordered table-header-sticky">
                              <thead>
                                <tr>
                                  <th style={{ width: '50%' }}>
                                    <div>{t('indicatorName')}</div>
                                  </th>
                                  <th style={{ width: '10%' }}>
                                    <span>{t('lastValue')}</span>
                                  </th>
                                  <th style={{ width: '10%' }}>
                                    <span style={{ paddingLeft: '10px' }}>{t('newValue')}</span>
                                  </th>
                                  <th style={{ width: '5%' }}>
                                    <span style={{ paddingLeft: '0px' }}>{t('na')}</span>
                                  </th>
                                  <th style={{ width: '25%' }}>
                                    <span style={{ paddingLeft: '30px' }}>
                                      {t('timeLineIndicator')}
                                    </span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {values.topics.map((to) => (
                                  <>
                                    <tr key={`tr_${to.id}`} className="td-rationale">
                                      <td colSpan={5}>
                                        <h5 className="table-indicator-title title ">
                                          {to.description}
                                        </h5>
                                      </td>
                                    </tr>
                                    {values.indicators
                                      .filter((e) => e.espsId === to.id)
                                      .map((x) => (
                                        <>
                                          <tr
                                            key={`tr_${x.esgIndicatorId}`}
                                            className={x.requiredRationale ? 'td-rationale' : ''}
                                          >
                                            <td className="table-text indicator-description">
                                              <h5 className="table-indicator-subtitle title">
                                                {x.name}
                                              </h5>
                                              {x.description}
                                            </td>
                                            <td
                                              className={`${
                                                x.typeValue === TypeValue.HAZARDS &&
                                                x.lastValue !== ConstValue.NA &&
                                                x.lastValue !== ''
                                                  ? 'indicator-last-value-dropdown'
                                                  : ''
                                              }`}
                                            >
                                              {x.typeValue === TypeValue.HAZARDS &&
                                              x.lastValue !== ConstValue.NA &&
                                              x.lastValue !== '' ? (
                                                <FormDropdown
                                                  clickable={false}
                                                  name={`HazardsLastValues${x.esgIndicatorId}`}
                                                  elements={x.lastValue.split(',').map((value) => ({
                                                    id: 0,
                                                    text: value,
                                                    value,
                                                  }))}
                                                  value={
                                                    x.lastValue && x.lastValue.split(',')[0]
                                                      ? x.lastValue.split(',')[0]
                                                      : ''
                                                  }
                                                  className={`no-modal dropdown-indicator ${
                                                    !x.enabled ? DROPDOWN_DISABLED_CLASS : ''
                                                  }`}
                                                  disabled={!x.enabled}
                                                />
                                              ) : (
                                                <LastValueButton
                                                  key="btnLast"
                                                  className="btn-last-value"
                                                  text={renderLastValue(x.lastValue, x.typeValue)}
                                                  type="button"
                                                  disabled={x.lastValue.length === 0}
                                                />
                                              )}
                                            </td>
                                            <td className="indicator-new-value">
                                              {x.typeValue === TypeValue.INTEGER && (
                                                <div>
                                                  <input
                                                    id={`NewValue${x.esgIndicatorId}`}
                                                    placeholder={determinePlaceholder(
                                                      x.enabled,
                                                      formik.values.indicators[x.formId].newValue
                                                    )}
                                                    name={`NewValue${x.esgIndicatorId}`}
                                                    min="0"
                                                    maxLength={10}
                                                    max={9999999999}
                                                    size={10}
                                                    onKeyDown={(evt) =>
                                                      evt.key === 'e' && evt.preventDefault()
                                                    }
                                                    className={getInputClass(
                                                      x.typeValue,
                                                      x.newValue
                                                    )}
                                                    disabled={
                                                      !x.enabled ||
                                                      formik.values.indicators[x.formId]
                                                        .newValue === ConstValue.NA
                                                    }
                                                    onChange={(selectedValue) => {
                                                      formik.setFieldValue(
                                                        `indicators[${x.formId}].newValue`,
                                                        selectedValue.target.value
                                                      );
                                                      setFormDirty(true);
                                                      onChangeValidation(
                                                        x.typeValue,
                                                        selectedValue.target.value,
                                                        x.formId
                                                      );
                                                    }}
                                                    value={x.newValue}
                                                    autoComplete="off"
                                                  />
                                                </div>
                                              )}
                                              {x.typeValue === TypeValue.DECIMALS2 && (
                                                <div>
                                                  <input
                                                    id={`NewValue${x.esgIndicatorId}`}
                                                    placeholder={
                                                      !x.belongsCalculator
                                                        ? determinePlaceholder(
                                                            x.enabled,
                                                            formik.values.indicators[x.formId]
                                                              .newValue
                                                          )
                                                        : ''
                                                    }
                                                    name={`NewValue${x.esgIndicatorId}`}
                                                    min="0"
                                                    maxLength={15}
                                                    max={999999999999.99}
                                                    size={15}
                                                    onKeyDown={(evt) =>
                                                      evt.key === 'e' && evt.preventDefault()
                                                    }
                                                    className={`${getInputClass(
                                                      x.typeValue,
                                                      x.newValue
                                                    )} ${
                                                      x.belongsCalculator
                                                        ? 'input-ghg-calculator'
                                                        : ''
                                                    }`}
                                                    disabled={
                                                      !x.enabled ||
                                                      formik.values.indicators[x.formId]
                                                        .newValue === ConstValue.NA ||
                                                      x.belongsCalculator
                                                    }
                                                    onChange={(selectedValue) => {
                                                      formik.setFieldValue(
                                                        `indicators[${x.formId}].newValue`,
                                                        selectedValue.target.value
                                                      );
                                                      setFormDirty(true);
                                                      onChangeValidation(
                                                        x.typeValue,
                                                        selectedValue.target.value,
                                                        x.formId
                                                      );
                                                    }}
                                                    autoComplete="off"
                                                    value={x.newValue}
                                                  />
                                                </div>
                                              )}
                                              {x.typeValue === TypeValue.PERCENT && (
                                                <div>
                                                  <input
                                                    id={`NewValue${x.esgIndicatorId}`}
                                                    placeholder={determinePlaceholder(
                                                      x.enabled,
                                                      formik.values.indicators[x.formId].newValue
                                                    )}
                                                    name={`NewValue${x.esgIndicatorId}`}
                                                    min="0"
                                                    maxLength={10}
                                                    max={99999999.99}
                                                    size={10}
                                                    onKeyDown={(evt) =>
                                                      evt.key === 'e' && evt.preventDefault()
                                                    }
                                                    className={getInputClass(
                                                      x.typeValue,
                                                      x.newValue
                                                    )}
                                                    disabled={
                                                      !x.enabled ||
                                                      formik.values.indicators[x.formId]
                                                        .newValue === ConstValue.NA
                                                    }
                                                    onChange={(selectedValue) => {
                                                      formik.setFieldValue(
                                                        `indicators[${x.formId}].newValue`,
                                                        selectedValue.target.value
                                                      );
                                                      setFormDirty(true);
                                                      onChangeValidation(
                                                        x.typeValue,
                                                        selectedValue.target.value,
                                                        x.formId
                                                      );
                                                    }}
                                                    autoComplete="off"
                                                    value={x.newValue}
                                                  />
                                                </div>
                                              )}
                                              {x.typeValue === TypeValue.SPR && (
                                                <FormDropdown
                                                  name={`SprNewValue${x.esgIndicatorId}`}
                                                  elements={sprElements}
                                                  value={
                                                    x.enabled
                                                      ? values.indicators[x.formId].newValue
                                                      : ''
                                                  }
                                                  className={`no-modal dropdown-indicator ${
                                                    !x.enabled ? DROPDOWN_DISABLED_CLASS : ''
                                                  }`}
                                                  onChangeAction={(selectedValue) => {
                                                    formik.setFieldValue(
                                                      `indicators[${x.formId}].newValue`,
                                                      selectedValue
                                                    );
                                                    setFormDirty(true);
                                                  }}
                                                  disabled={!x.enabled}
                                                />
                                              )}
                                              {x.typeValue === TypeValue.YESNO && (
                                                <FormDropdown
                                                  name={`YesNoNewValue${x.esgIndicatorId}`}
                                                  elements={
                                                    values.indicators[x.formId].newValue ===
                                                    ConstValue.NA
                                                      ? optionNa
                                                      : yesNoElements
                                                  }
                                                  value={
                                                    x.enabled
                                                      ? values.indicators[x.formId].newValue
                                                      : ''
                                                  }
                                                  className={`no-modal dropdown-indicator ${
                                                    !x.enabled ? DROPDOWN_DISABLED_CLASS : ''
                                                  }`}
                                                  onChangeAction={(selectedValue) => {
                                                    formik.setFieldValue(
                                                      `indicators[${x.formId}].newValue`,
                                                      selectedValue
                                                    );
                                                    setFormDirty(true);

                                                    if (x.name === 'ESGI 4.1') {
                                                      onChangeValidation41(
                                                        values.indicators[x.formId].newValue,
                                                        x.formId
                                                      );
                                                    }
                                                  }}
                                                  disabled={
                                                    !x.enabled ||
                                                    formik.values.indicators[x.formId].newValue ===
                                                      ConstValue.NA
                                                  }
                                                />
                                              )}
                                              {x.typeValue === TypeValue.Z07 && (
                                                <FormDropdown
                                                  name={`07NewValue${x.esgIndicatorId}`}
                                                  elements={number07}
                                                  value={
                                                    x.enabled
                                                      ? values.indicators[x.formId].newValue
                                                      : ''
                                                  }
                                                  className={`no-modal dropdown-indicator ${
                                                    !x.enabled ? DROPDOWN_DISABLED_CLASS : ''
                                                  }`}
                                                  onChangeAction={(selectedValue) => {
                                                    formik.setFieldValue(
                                                      `indicators[${x.formId}].newValue`,
                                                      selectedValue
                                                    );
                                                    setFormDirty(true);
                                                  }}
                                                  disabled={!x.enabled}
                                                />
                                              )}
                                              {x.typeValue === TypeValue.HAZARDS && (
                                                <CustomDropDown
                                                  text={setHazardsInitialForm(
                                                    values.indicators[x.formId].newValue
                                                  )}
                                                  className="no-modal dropdown-indicator"
                                                  disabled={
                                                    !x.enabled ||
                                                    formik.values.indicators[x.formId].newValue ===
                                                      ConstValue.NA
                                                  }
                                                >
                                                  <OptionChecks
                                                    cells={hazardsElements}
                                                    checkeds={checkedHazards}
                                                    setCheckeds={(id, checked) => {
                                                      formik.setFieldValue(
                                                        `indicators[${x.formId}].newValue`,
                                                        setCheckedValues(id, checked)
                                                      );

                                                      setFormDirty(true);
                                                    }}
                                                  />
                                                </CustomDropDown>
                                              )}
                                              {customValidation(x.typeValue, x.newValue) !== true &&
                                                x.newValue !== '' && (
                                                  <span className="form__form-group-error-input">
                                                    {customValidation(x.typeValue, x.newValue)}
                                                  </span>
                                                )}
                                              {x.name === 'ESGI 4.1' &&
                                                Validation41(x.newValue) !== true &&
                                                x.newValue !== '' && (
                                                  <span className="form__form-group-error-input">
                                                    {Validation41(x.newValue)}
                                                  </span>
                                                )}
                                            </td>
                                            <td className="indicator-new-value-na">
                                              {' '}
                                              {(x.typeValue === TypeValue.INTEGER ||
                                                x.typeValue === TypeValue.DECIMALS2 ||
                                                x.typeValue === TypeValue.PERCENT ||
                                                x.typeValue === TypeValue.HAZARDS ||
                                                x.typeValue === TypeValue.YESNO) && (
                                                <div>
                                                  <CheckBox
                                                    className="checkbox-indicators"
                                                    element={{
                                                      id: 'na',
                                                      text: t(''),
                                                      checked:
                                                        formik.values.indicators[x.formId]
                                                          .newValue === ConstValue.NA,
                                                    }}
                                                    onChange={(ids: string, value: boolean) => {
                                                      const newValue = value ? ConstValue.NA : '';
                                                      formik.setFieldValue(
                                                        `indicators[${x.formId}].newValue`,
                                                        newValue
                                                      );
                                                      if (x.name === 'ESGI 4.1') {
                                                        onChangeValidation41(newValue, x.formId);
                                                      }
                                                    }}
                                                    disabled={
                                                      !x.enabled ||
                                                      (x.belongsCalculator &&
                                                        x.newValue !== '' &&
                                                        x.newValue !== ConstValue.NA)
                                                    }
                                                  />
                                                </div>
                                              )}
                                            </td>
                                            <td className="td-timeline">
                                              <IndicatorTimeline
                                                timelineContainer={x.timeLineContainer}
                                                timelineWidth={500}
                                              />
                                            </td>
                                          </tr>
                                          <>
                                            {values.indicators[x.formId].belongsCalculator && (
                                              <tr className="td-rationale">
                                                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                                                <td colSpan={2} className="td-rationale" />
                                                <td className="indicator-ghg-btn">
                                                  <div>
                                                    <Button
                                                      key="btnCalc"
                                                      className="btn-primary btn-indicator-calc"
                                                      text={t('ghgCalculator')}
                                                      type="button"
                                                      disabled={!x.enabled}
                                                      onClick={() => {
                                                        navigate(
                                                          `/operations/${operation.number}/calculator`
                                                        );
                                                      }}
                                                    />
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                            {values.indicators[x.formId].requiredRationale ===
                                              true && (
                                              <tr>
                                                <td colSpan={5} className="td-rationale">
                                                  <div
                                                    className={`table-indicator-rationale ${
                                                      !x.enabled
                                                        ? 'table-text-indicator-disabled'
                                                        : ''
                                                    }`}
                                                  >
                                                    <div
                                                      className={`table-indicator-title-rationale title ${
                                                        !x.enabled
                                                          ? 'table-text-indicator-disabled'
                                                          : ''
                                                      }`}
                                                    >
                                                      {t('providedJustification')}{' '}
                                                    </div>
                                                    <Textarea
                                                      name={`Rationae${x.esgIndicatorId}`}
                                                      placeholder={t('maxLength', {
                                                        number: 750,
                                                      })}
                                                      scrollable
                                                      maxLength={750}
                                                      onChange={(selectedValue) => {
                                                        formik.setFieldValue(
                                                          `indicators[${x.formId}].rationale`,
                                                          selectedValue.target.value
                                                        );
                                                        setFormDirty(true);
                                                      }}
                                                      value={
                                                        values.indicators[x.formId].rationale !=
                                                        null
                                                          ? values.indicators[x.formId].rationale
                                                          : ''
                                                      }
                                                      disabled={
                                                        values.indicators[x.formId].newValue ===
                                                          '' || !values.indicators[x.formId].enabled
                                                      }
                                                      className={
                                                        x.requiredRationale &&
                                                        x.enabled &&
                                                        formik.errors.indicators &&
                                                        formik.touched.indicators &&
                                                        formik.touched.indicators[x.formId]
                                                          .rationale &&
                                                        formik.values.indicators[x.formId]
                                                          .newValue !== null &&
                                                        formik.values.indicators[x.formId]
                                                          .newValue !== '' &&
                                                        (formik.values.indicators[x.formId]
                                                          .rationale === null ||
                                                          formik.values.indicators[
                                                            x.formId
                                                          ].rationale.trim() === '')
                                                          ? 'form-error-box'
                                                          : ''
                                                      }
                                                    />
                                                  </div>
                                                  {x.requiredRationale &&
                                                    x.enabled &&
                                                    formik.errors.indicators &&
                                                    formik.touched.indicators &&
                                                    formik.touched.indicators[x.formId].rationale &&
                                                    formik.values.indicators[x.formId].newValue !==
                                                      null &&
                                                    formik.values.indicators[x.formId].newValue !==
                                                      '' &&
                                                    (formik.values.indicators[x.formId]
                                                      .rationale === null ||
                                                      formik.values.indicators[
                                                        x.formId
                                                      ].rationale.trim() === '') && (
                                                      <span className="form__form-group-error-indicator-rationale">
                                                        Required
                                                      </span>
                                                    )}
                                                </td>
                                              </tr>
                                            )}
                                          </>
                                        </>
                                      ))}
                                  </>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                          {formik.errors.indicators && formik.touched.indicators && (
                            <div className="form__form-group-error-indicators">
                              Check the required justifications
                            </div>
                          )}
                          {canEdit && (
                            <ButtonToolbar className="w-100 py-3 justify-content-end">
                              <Spinner loading={isSubmitting} />
                              <Button
                                key="btnCancel"
                                className="btn-secondary"
                                onClick={discardChanges}
                                text="Save indicators before send"
                                type="button"
                                disabled={isSubmitting || !dirty || indicatorWithErrors.length > 0}
                              />
                              <Button
                                key="btnSubmit"
                                className="btn-primary"
                                text="Send Indicators"
                                type="submit"
                                disabled={
                                  isSubmitting ||
                                  !formik.values.indicators.some(
                                    (indicator) => indicator.newValue !== ''
                                  ) ||
                                  indicatorWithErrors.length > 0
                                }
                              />
                            </ButtonToolbar>
                          )}
                        </form>
                        <ConfirmModal
                          visible={showDiscardModal}
                          buttonText="Save indicators before send"
                          setVisible={setShowDiscardModal}
                          action={() => saveIndicators(formik)}
                        >
                          {t('Are you sure you save the changes?')}
                        </ConfirmModal>
                      </>
                    );
                  }}
                </Formik>
              )}
              {!data &&
                !loading1 &&
                !loading &&
                (canEdit ? (
                  <div className="m-3 d-flex justify-content-center">
                    <h1 className="page-title inner-title">
                      {t('operationNoToolkitIndicator', { number: operation.number })}
                    </h1>
                  </div>
                ) : (
                  <div className="m-3 d-flex justify-content-center">
                    <h1 className="page-title inner-title">
                      {t('operationNoToolkitIndicatorNoMember', { number: operation.number })}
                    </h1>
                  </div>
                ))}
            </SpinnerArea>
          </Section>
        </Row>
        <Modal
          visible={showErrorModal}
          setVisible={setShowErrorModal}
          title={t('adminMembersAssignedToOperationMessage')}
          customClass="extra_wide-modal"
          actions={[{ text: 'Close', action: () => setShowErrorModal(false) }]}
        >
          <div className="d-flex sticky-section">
            <div className="table-responsive table-header-sticky px-3">
              <Table>
                <thead>
                  <tr>
                    <th>{t('userName')}</th>
                    <th>{t('operation')}</th>
                  </tr>
                </thead>
                <tbody>{renderErrorTable()}</tbody>
              </Table>
            </div>
          </div>
        </Modal>
        <ConfirmModal
          visible={showConfirmModal}
          buttonText={t('confirmChanges')}
          setVisible={setShowConfirmModal}
          action={sendIndicators}
        >
          {t('confirmChangesMessage')}
        </ConfirmModal>
      </Container>
    </>
  );
};

export default Indicator;
